import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserRecipes, getCollections, createCollection, addRecipeToCollection, removeRecipeFromCollection, getRecipeLineage, deleteRecipe } from '../services/api';
import { getDefaultRecipeImage } from '../utils/defaultRecipeImages';
import { useAuth } from '../context/authContext';
import { useMenuScheduling } from '../context/menuSchedulingContext';
import { useCompare } from '../context/CompareContext';
import { PageHeading, Heading, Text, PopupWindow, Modal } from '../components/ui';
import SignedImage from '../components/SignedImage';
import PixelArtLoader from '../components/PixelArtLoader';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import EducationModal from '../components/EducationModal';
import FloatingCreateButton from '../components/FloatingCreateButton';

// Valid categories list
const VALID_CATEGORIES = [
    'Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Snack', 
    'Dessert', 'Supper', 'Appetizer', 'Side', 'Drink', 
    'Condiment', 'Bread', 'Salad', 'Soup'
];

const Recipes = () => {
    const { user, userProfile } = useAuth();
    const { isSchedulingMode, toggleRecipeSelection, selectedRecipeIds } = useMenuScheduling();
    const { showCompareSelectionModal, toggleRecipeSelection: toggleCompareRecipe, selectedRecipes: compareSelectedRecipes } = useCompare();
    const navigate = useNavigate();
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem('myRecipes_viewMode') || 'grid';
    }); // 'grid' or 'list'
    const [contentView, setContentView] = useState(() => {
        return localStorage.getItem('myRecipes_contentView') || 'recipes';
    }); // 'recipes' or 'collections'
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem('myRecipes_searchTerm') || '';
    });
    const [filteredRecipes, setFilteredRecipes] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(() => {
        return localStorage.getItem('myRecipes_selectedCategory') || '';
    });
    const [categories, setCategories] = useState([]);
    const [controlsExpanded, setControlsExpanded] = useState(() => {
        return localStorage.getItem('myRecipes_controlsExpanded') === 'true';
    });
    
    // Multi-select state
    const [selectedRecipes, setSelectedRecipes] = useState(new Set());
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [recipesToDelete, setRecipesToDelete] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);
    const [addingToCollection, setAddingToCollection] = useState(false);
    
    // Track which recipe cards are showing gold standard
    const [showingGoldStandard, setShowingGoldStandard] = useState(new Map());
    
    // Collection state
    const [selectedCollection, setSelectedCollection] = useState(() => {
        return localStorage.getItem('myRecipes_selectedCollection') || '';
    });
    const [collections, setCollections] = useState([]);
    const [showAddCollection, setShowAddCollection] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');
    const [showSearch, setShowSearch] = useState(() => {
        return localStorage.getItem('myRecipes_searchTerm') ? true : false;
    });
    
    // Popup state
    const [openPopups, setOpenPopups] = useState([]);
    
    // Education modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showBasesModal, setShowBasesModal] = useState(false);
    const controlsPanelId = 'my-recipes-controls-panel';
    
    // Lineage state
    const [lineageData, setLineageData] = useState({});
    const [recipeLineageMap, setRecipeLineageMap] = useState({}); // Maps recipe ID to its lineage chain

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Function to handle lineage arrow click - navigate to parent recipe
    const handleLineageArrowClick = async (recipeId, e) => {
        e.preventDefault(); // Prevent default behavior
        e.stopPropagation(); // Prevent card click
        
        try {
            // Get the current recipe to determine if we're in a lineage view
            const currentRecipe = recipes.find(r => r.id === recipeId);
            const isLineageView = currentRecipe?.isLineageView;
            
            // Fetch lineage data if not already cached
            const cacheKey = isLineageView ? `${currentRecipe.originalRecipeId}-${recipeId}` : recipeId;
            if (!lineageData[cacheKey]) {
                const data = await getRecipeLineage(recipeId);
                setLineageData(prev => ({
                    ...prev,
                    [cacheKey]: data
                }));
            }
            
            // Navigate to parent recipe - only update the current card, don't add new recipes
            if (lineageData[cacheKey] && lineageData[cacheKey].parent) {
                const parentRecipe = lineageData[cacheKey].parent;
                
                // Clean the current recipe data to remove any lineage-related properties before storing
                const cleanCurrentRecipe = {
                    ...currentRecipe,
                    isLineageView: false,
                    originalRecipeId: undefined,
                    originalRecipeData: undefined
                };
                
                // Update the current recipe card with parent data, maintaining position and styling
                setRecipes(prev => {
                    // First, remove any recipe that has the same ID as the parent recipe (if it exists separately)
                    const filteredRecipes = prev.filter(recipe => recipe.id !== parentRecipe.id || recipe.id === recipeId);
                    
                    // Then update the current recipe with parent data
                    const updatedRecipes = filteredRecipes.map(recipe => 
                        recipe.id === recipeId 
                            ? { 
                                ...parentRecipe, 
                                id: recipeId, // Keep original recipe ID for card positioning
                                isLineageView: true, 
                                originalRecipeId: isLineageView ? currentRecipe.originalRecipeId : recipeId,
                                originalRecipeData: isLineageView ? currentRecipe.originalRecipeData : cleanCurrentRecipe, // Store clean original recipe data for navigation back
                                // Map the correct fields for display
                                author: parentRecipe.created_by_name || 'Unknown',
                                creator_name: parentRecipe.created_by_name || 'Unknown',
                                // Parse ingredients if they exist as a string
                                ingredients: typeof parentRecipe.ingredients === 'string' 
                                    ? JSON.parse(parentRecipe.ingredients || '[]') 
                                    : (parentRecipe.ingredients || []),
                                // Ensure fork information is preserved
                                forks_count: parentRecipe.forks_count || 0
                            }
                            : recipe
                    );
                    
                    return updatedRecipes;
                });
            }
        } catch (error) {
            console.error('Failed to fetch lineage data:', error);
        }
    };

    // Function to check if arrow should be shown
    const shouldShowLineageArrow = (recipe) => {
        // Show for forked recipes (either original or lineage view)
        if (!recipe.parent_recipe_id) return false;
        
        // Don't show if this recipe was created by the current user (they are the original creator)
        // This means the user created this recipe, so they shouldn't see an arrow to go "up" the chain
        if (recipe.creator_id === user?.uid || recipe.author_id === user?.uid) {
            return false;
        }
        
        // Don't show arrows on other recipes if we're already viewing a parent recipe
        const isAnyRecipeInLineageView = recipes.some(r => r.isLineageView);
        if (isAnyRecipeInLineageView && !recipe.isLineageView) {
            return false;
        }
        
        return true;
    };

    // Function to navigate back to original recipe
    const handleBackToOriginal = (originalRecipeId, e) => {
        e.preventDefault(); // Prevent default behavior
        e.stopPropagation(); // Prevent card click
        
        // Find the lineage view recipe and restore its original data
        const lineageViewRecipe = recipes.find(r => r.isLineageView && r.originalRecipeId === originalRecipeId);
        if (lineageViewRecipe && lineageViewRecipe.originalRecipeData) {
            // Clean the original recipe data to remove any lineage-related properties
            const cleanOriginalData = {
                ...lineageViewRecipe.originalRecipeData,
                isLineageView: false,
                originalRecipeId: undefined,
                originalRecipeData: undefined
            };
            
            setRecipes(prev => {
                const updatedRecipes = prev.map(recipe => 
                    recipe.id === originalRecipeId
                        ? cleanOriginalData
                        : recipe
                );
                
                // Ensure we don't have duplicate recipes after restoration
                const uniqueRecipes = updatedRecipes.filter((recipe, index, self) => 
                    index === self.findIndex(r => r.id === recipe.id)
                );
                
                return uniqueRecipes;
            });
        }
    };

    const fetchRecipes = async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        try {
            const token = await user.getIdToken();
            const userRecipes = await getUserRecipes(user.uid, token);
            setRecipes(userRecipes);
            setFilteredRecipes(userRecipes);
        } catch (err) {
            console.error('Error fetching user recipes:', err);
            setError('Failed to load your recipes');
        } finally {
            setLoading(false);
        }
    };

    const fetchCollections = async () => {
        if (!user) return;

        try {
            const token = await user.getIdToken();
            const collectionsData = await getCollections(token);
            setCollections(collectionsData);
        } catch (err) {
            console.error('Error fetching collections:', err);
            // Don't set error state for collections as it's not critical
        }
    };

    useEffect(() => {
        fetchRecipes();
        fetchCollections();
    }, [user]);
    
    // Refresh recipes when window regains focus (e.g., after updating gold standard on another page)
    useEffect(() => {
        const handleFocus = () => {
            if (user) {
                fetchRecipes();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [user]);

    // Persist view mode to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_viewMode', viewMode);
    }, [viewMode]);

    // Persist content view to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_contentView', contentView);
    }, [contentView]);

    // Persist controls expanded state to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_controlsExpanded', controlsExpanded.toString());
    }, [controlsExpanded]);

    // Persist search term to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_searchTerm', searchTerm);
    }, [searchTerm]);

    // Persist selected category to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_selectedCategory', selectedCategory);
    }, [selectedCategory]);

    // Persist selected collection to localStorage
    useEffect(() => {
        localStorage.setItem('myRecipes_selectedCollection', selectedCollection);
    }, [selectedCollection]);

    // Filter recipes based on search term
    useEffect(() => {
        // Use all valid categories instead of just those from recipes
        setCategories(VALID_CATEGORIES);
    }, []);

    useEffect(() => {
        // Don't filter if we don't have recipes yet
        if (recipes.length === 0) {
            setFilteredRecipes([]);
            setIsFiltering(false);
            return;
        }

        // Set filtering state immediately to prevent flash
        setIsFiltering(true);

        // Process filtering immediately without timeout to prevent flash
        let filtered = recipes;

        // Live Sessions v2: Show Gold Standard recipes instead of newest forks
        // Group recipes by chain_root_id and prioritize Gold Standard (is_gold_standard = true)
        const chainGroups = new Map();
        filtered.forEach(recipe => {
            const chainId = recipe.chain_root_id;
            const existingRecipe = chainGroups.get(chainId);
            
            if (!existingRecipe) {
                chainGroups.set(chainId, recipe);
            } else {
                // Prioritize Gold Standard recipes
                const isCurrentGoldStandard = recipe.is_gold_standard === true;
                const isExistingGoldStandard = existingRecipe.is_gold_standard === true;
                
                if (isCurrentGoldStandard && !isExistingGoldStandard) {
                    // Current recipe is Gold Standard, existing is not - use current
                    chainGroups.set(chainId, recipe);
                } else if (!isCurrentGoldStandard && isExistingGoldStandard) {
                    // Existing recipe is Gold Standard, current is not - keep existing
                    // Do nothing, keep existing
                } else {
                    // Both or neither are Gold Standard - use most recent
                    if (new Date(recipe.created_at) > new Date(existingRecipe.created_at)) {
                        chainGroups.set(chainId, recipe);
                    }
                }
            }
        });
        
        // Convert back to array with Gold Standard recipes prioritized
        filtered = Array.from(chainGroups.values());

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(recipe =>
                (recipe.title && recipe.title.toLowerCase().includes(lowerSearch)) ||
                (recipe.name && recipe.name.toLowerCase().includes(lowerSearch)) ||
                (recipe.description && recipe.description.toLowerCase().includes(lowerSearch))
            );
        }

        if (selectedCategory) {
            filtered = filtered.filter(recipe =>
                recipe.category && recipe.category.toLowerCase() === selectedCategory.toLowerCase()
            );
        }

        if (selectedCollection) {
            if (selectedCollection === 'no-collection') {
                filtered = filtered.filter(recipe => !recipe.collection_id);
            } else {
                filtered = filtered.filter(recipe => recipe.collection_id === selectedCollection);
            }
        }

        setFilteredRecipes(filtered);
        setIsFiltering(false);
    }, [recipes, searchTerm, selectedCategory, selectedCollection]);

    // Close all popups when ESC is pressed
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && openPopups.length > 0) {
                // Close the most recent popup
                setOpenPopups(prev => prev.slice(0, -1));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [openPopups.length]);

    const hasActiveFilters = Boolean(searchTerm || selectedCategory || selectedCollection);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedCollection('');
        // Clear from localStorage as well
        localStorage.removeItem('myRecipes_searchTerm');
        localStorage.removeItem('myRecipes_selectedCategory');
        localStorage.removeItem('myRecipes_selectedCollection');
    };

    // Collection management functions
    const addCollection = async () => {
        const trimmedName = newCollectionName.trim();
        
        if (!trimmedName) {
            alert('Please enter a collection name.');
            return;
        }
        
        if (!user) {
            alert('You must be logged in to create collections.');
            return;
        }
        
        try {
            const token = await user.getIdToken();
            const newCollection = await createCollection({ name: trimmedName }, token);
            setCollections(prev => [...prev, newCollection]);
            setNewCollectionName('');
            setShowAddCollection(false);
        } catch (err) {
            console.error('Error creating collection:', err);
            if (err.message && err.message.includes('already exists')) {
                alert('A collection with this name already exists. Please choose a different name.');
            } else {
                alert(`Failed to create collection: ${err.message || 'Please try again.'}`);
            }
        }
    };

    const addToCollection = async (recipeId, collectionId) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await addRecipeToCollection(collectionId, recipeId, token);
            setRecipes(prev => prev.map(recipe => 
                recipe.id === recipeId 
                    ? { ...recipe, collection_id: collectionId }
                    : recipe
            ));
        } catch (err) {
            console.error('Error adding recipe to collection:', err);
            alert('Failed to add recipe to collection. Please try again.');
        }
    };

    const removeFromCollection = async (recipeId) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await removeRecipeFromCollection(recipeId, token);
            setRecipes(prev => prev.map(recipe => 
                recipe.id === recipeId 
                    ? { ...recipe, collection_id: null }
                    : recipe
            ));
        } catch (err) {
            console.error('Error removing recipe from collection:', err);
            alert('Failed to remove recipe from collection. Please try again.');
        }
    };

    // Multi-select functionality
    const toggleMultiSelectMode = () => {
        setIsMultiSelectMode(!isMultiSelectMode);
        if (isMultiSelectMode) {
            setSelectedRecipes(new Set());
            setShowBulkActions(false);
        }
    };

    const toggleMultiSelectRecipe = (recipeId) => {
        const newSelected = new Set(selectedRecipes);
        if (newSelected.has(recipeId)) {
            newSelected.delete(recipeId);
        } else {
            newSelected.add(recipeId);
        }
        setSelectedRecipes(newSelected);
        setShowBulkActions(newSelected.size > 0);
    };

    const selectAllRecipes = () => {
        const allRecipeIds = new Set(filteredRecipes.map(recipe => recipe.id));
        setSelectedRecipes(allRecipeIds);
        setShowBulkActions(true);
    };

    const clearSelection = () => {
        setSelectedRecipes(new Set());
        setShowBulkActions(false);
    };

    // Bulk operations
    const handleBulkAddToCollection = () => {
        if (selectedRecipes.size === 0) return;
        setShowAddToCollectionModal(true);
    };

    const handleBulkDelete = () => {
        if (selectedRecipes.size === 0) return;
        
        const recipesToDeleteList = filteredRecipes.filter(recipe => selectedRecipes.has(recipe.id));
        setRecipesToDelete(recipesToDeleteList);
        setShowDeleteConfirm(true);
    };

    const confirmBulkDelete = async () => {
        if (!user || recipesToDelete.length === 0) return;
        
        try {
            setDeleting(true);
            const token = await user.getIdToken();
            
            // Delete each recipe
            for (const recipe of recipesToDelete) {
                await deleteRecipe(recipe.id, token);
            }
            
            // Refresh recipes and clear selection
            await fetchRecipes();
            setSelectedRecipes(new Set());
            setShowBulkActions(false);
            setShowDeleteConfirm(false);
            setRecipesToDelete([]);
        } catch (err) {
            console.error('Error deleting recipes:', err);
            alert('Failed to delete some recipes. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleBulkAddToCollectionConfirm = async () => {
        if (!user || !selectedCollection || selectedRecipes.size === 0) return;
        
        try {
            setAddingToCollection(true);
            const token = await user.getIdToken();
            
            // Add each selected recipe to the collection
            for (const recipeId of selectedRecipes) {
                await addRecipeToCollection(selectedCollection, recipeId, token);
            }
            
            // Refresh recipes and clear selection
            await fetchRecipes();
            setSelectedRecipes(new Set());
            setShowBulkActions(false);
            setShowAddToCollectionModal(false);
        } catch (err) {
            console.error('Error adding recipes to collection:', err);
            alert('Failed to add some recipes to collection. Please try again.');
        } finally {
            setAddingToCollection(false);
        }
    };

    // Get recipes by collection (showing only most recent versions)
    const getRecipesByCollection = (collectionId) => {
        let collectionRecipes;
        if (collectionId === 'no-collection') {
            collectionRecipes = recipes.filter(recipe => !recipe.collection_id);
        } else {
            collectionRecipes = recipes.filter(recipe => recipe.collection_id === collectionId);
        }
        
        // Filter to show Gold Standard recipes for each recipe chain
        const chainGroups = new Map();
        collectionRecipes.forEach(recipe => {
            const chainId = recipe.chain_root_id;
            const existingRecipe = chainGroups.get(chainId);
            
            if (!existingRecipe) {
                chainGroups.set(chainId, recipe);
            } else {
                // Prioritize Gold Standard recipes
                const isCurrentGoldStandard = recipe.is_gold_standard === true;
                const isExistingGoldStandard = existingRecipe.is_gold_standard === true;
                
                if (isCurrentGoldStandard && !isExistingGoldStandard) {
                    // Current recipe is Gold Standard, existing is not - use current
                    chainGroups.set(chainId, recipe);
                } else if (!isCurrentGoldStandard && isExistingGoldStandard) {
                    // Existing recipe is Gold Standard, current is not - keep existing
                    // Do nothing, keep existing
                } else {
                    // Both or neither are Gold Standard - use most recent
                    if (new Date(recipe.created_at) > new Date(existingRecipe.created_at)) {
                        chainGroups.set(chainId, recipe);
                    }
                }
            }
        });
        
        return Array.from(chainGroups.values());
    };

    // Navigate to collection detail page
    const navigateToCollection = (collectionId) => {
        let collectionName;
        if (collectionId === 'no-collection') {
            collectionName = 'No Collection';
        } else {
            const collection = collections.find(c => c.id === collectionId);
            if (!collection) return;
            collectionName = collection.name;
        }
        
        navigate(`/collections/${collectionId}`, { 
            state: { 
                collectionName: collectionName,
                recipes: getRecipesByCollection(collectionId)
            }
        });
    };

    // Handle recipe row click to open popup (only in list mode)
    const handleRecipeClick = (recipe, e) => {
        // Don't open popup if clicking on action buttons
        if (e.target.closest('button') || e.target.closest('a')) {
            return;
        }
        
        // Don't open popups on small screens (mobile)
        if (window.innerWidth < 640) {
            // On small screens, navigate to recipe detail page
            navigate(`/recipes/${recipe.id}`);
            return;
        }
        
        // Only open popups in list mode, not grid mode
        if (viewMode !== 'list') {
            // In grid mode, navigate to recipe detail page using React Router
            navigate(`/recipes/${recipe.id}`);
            return;
        }
        
        // Only allow popups for recipes with images
        if (!recipe.images || recipe.images.length === 0) {
            // If no images, navigate to recipe detail page
            navigate(`/recipes/${recipe.id}`);
            return;
        }
        
        // Check if popup is already open for this recipe
        if (openPopups.some(popup => popup.recipe.id === recipe.id)) {
            return;
        }
        
        // Calculate diagonal position starting from current scroll position
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        const offset = openPopups.length * 32; // Increased offset for diagonal spacing
        
        // Start diagonal pattern from current scroll position
        const baseX = scrollX + 96; // Start 96px from left edge
        const baseY = scrollY + 96; // Start 96px from top edge
        
        // Create diagonal pattern: each popup moves down and right
        const left = baseX + offset;
        const top = baseY + offset;
        
        // Ensure popup doesn't go off-screen
        const popupWidth = 384; // w-96 = 384px
        const popupHeight = 400; // approximate height
        
        // If popup would go off the right edge, reset to left side but continue diagonal
        let finalLeft = left;
        let finalTop = top;
        
        if (left + popupWidth > window.innerWidth + scrollX - 24) {
            // Reset to left side, but continue diagonal pattern
            finalLeft = scrollX + 96;
            finalTop = top + 32; // Continue diagonal downward
        }
        
        // If popup would go off the bottom edge, reset to top but continue diagonal
        if (top + popupHeight > window.innerHeight + scrollY - 24) {
            // Reset to top, but continue diagonal pattern
            finalTop = scrollY + 96;
            finalLeft = left + 32; // Continue diagonal rightward
        }
        
        const newPopup = {
            recipe,
            id: Date.now(),
            style: {
                top: `${finalTop}px`,
                left: `${finalLeft}px`,
            }
        };
        
        setOpenPopups(prev => [...prev, newPopup]);
    };

    // Close specific popup
    const closePopup = (popupId) => {
        setOpenPopups(prev => prev.filter(popup => popup.id !== popupId));
    };


    const CollectionCard = ({ collection, collectionRecipes }) => {
        const hasRecipes = collectionRecipes.length > 0;
        
        // Get the first recipe's image for the collection
        const firstRecipe = collectionRecipes[0];
        const collectionImage = firstRecipe?.images && firstRecipe.images.length > 0 
            ? firstRecipe.images[0] 
            : null;
        
        return (
            <div className="relative group aspect-square mb-4">
                {/* Stacked window effect for collection cards - positioned under the main card */}
                <div className="absolute inset-0 bg-white hairline transform translate-x-3 translate-y-3 pointer-events-none group-hover:shadow-popup transition-all duration-200"></div>
                <div className="absolute inset-0 bg-white hairline transform translate-x-2 translate-y-2 pointer-events-none"></div>
                <div className="absolute inset-0 bg-white hairline transform translate-x-1 translate-y-1 pointer-events-none"></div>
                
                {/* Main collection card */}
                <div className="aspect-square bg-neutral-0 hairline transition-all duration-200 cursor-pointer group flex flex-col relative z-10"
                    onClick={() => navigateToCollection(collection.id)}
                >
                    <div className="flex-1 overflow-hidden relative z-10">
                        {collectionImage ? (
                            <SignedImage
                                src={collectionImage}
                                alt={collection.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                category={firstRecipe?.category}
                            />
                        ) : hasRecipes ? (
                            <SignedImage
                                src={getDefaultRecipeImage(firstRecipe?.category)}
                                alt={collection.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                category={firstRecipe?.category}
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col justify-center items-center bg-neutral-50">
                                <div className="text-4xl mb-4">📚</div>
                                <Text className="font-medium text-neutral-900 mb-2 group-hover:opacity-60 transition-opacity text-sm leading-tight text-center">
                                    {collection.name}
                                </Text>
                                <Text className="text-xs text-neutral-900 opacity-60 mb-2 text-center">
                                    {collectionRecipes.length} recipe{collectionRecipes.length !== 1 ? 's' : ''}
                                </Text>
                            </div>
                        )}
                    </div>
                    
                    <div className="p-3 flex-shrink-0 relative z-10">
                        <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight">
                            {collection.name}
                        </Text>
                        
                        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider">
                            <span className="text-neutral-900 opacity-60">COLLECTION</span>
                            <span className="text-blue-600 font-medium">VIEW</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const RecipeGridCard = ({ recipe, allRecipes }) => {
        // Check if this card is showing gold standard
        const isShowingGoldStandard = showingGoldStandard.has(recipe.id);
        const displayedRecipe = isShowingGoldStandard ? showingGoldStandard.get(recipe.id) : recipe;
        
        // Use allRecipes (full recipes array) instead of recipes state for finding gold standard
        const recipesToSearch = allRecipes || recipes;
        
        // Parse ingredients and instructions if they're JSON strings
        const parsedIngredients = typeof displayedRecipe.ingredients === 'string' ? 
            JSON.parse(displayedRecipe.ingredients || '[]') : (displayedRecipe.ingredients || []);
        
        // Determine display name and styling for creator/forker/transfer
        // Show stack effect for: 1) forked recipes, 2) root recipes that have forks
        // Check multiple indicators to ensure fork status is detected:
        // - is_fork flag from version
        // - forker_name (set when version_is_fork is true)
        // - parent_recipe_id (indicates this is a fork)
        // - Not original
        // CRITICAL: If a recipe has parent_recipe_id, it's a fork (regardless of is_transfer)
        // This ensures forks are correctly identified even if is_transfer was incorrectly preserved
        // Get forks_count with fallback
        const recipeForksCount = displayedRecipe.forks_count ?? recipe.forks_count ?? 0;
        
        // CRITICAL: A recipe with parent_recipe_id is ALWAYS a fork, even if is_transfer is true
        // This handles the case where is_transfer was incorrectly preserved from the source recipe
        // parent_recipe_id is the definitive indicator that a recipe is a fork
        const hasParentRecipe = displayedRecipe.parent_recipe_id && !displayedRecipe.is_original;
        
        // Check other fork indicators
        const hasForkIndicators = displayedRecipe.is_fork || displayedRecipe.forker_name;
        
        const isForked = (
            (hasParentRecipe || (hasForkIndicators && !displayedRecipe.is_original && !displayedRecipe.is_transfer)) && 
            !displayedRecipe.isLineageView
        ) || 
        (displayedRecipe.is_original && recipeForksCount > 0 && !displayedRecipe.isLineageView);
        
        // Only show as transferred if it's NOT a fork (forks should never show as transfers)
        const isTransferred = displayedRecipe.is_transfer && !hasParentRecipe && !hasForkIndicators;
        // For root recipes with forks, show original creator name, not forker name
        const isRootWithForks = displayedRecipe.is_original && recipeForksCount > 0 && !displayedRecipe.isLineageView;
        // Get forker name - use created_by_name if forker_name is not set but recipe is a fork
        const forkerName = displayedRecipe.forker_name || 
                          (isForked && displayedRecipe.created_by_name ? displayedRecipe.created_by_name : null);
        
        const displayName = isRootWithForks ? (displayedRecipe.author || displayedRecipe.creator_name || 'Unknown') : 
                           (isForked ? (forkerName || displayedRecipe.created_by_name || 'Unknown') : 
                           (isTransferred ? displayedRecipe.creator_name : (displayedRecipe.author || displayedRecipe.creator_name || 'Unknown')));
        const nameStyle = isForked ? 'text-blue-600' : (isTransferred ? 'text-blue-600' : 'text-neutral-900 opacity-60');
        
        const isSelected = selectedRecipes.has(displayedRecipe.id);
        const isSchedulingSelected = isSchedulingMode && selectedRecipeIds.has(displayedRecipe.id);
        const isCompareSelected = showCompareSelectionModal && compareSelectedRecipes.some(r => r.id === displayedRecipe.id);
        
        // Find gold standard recipe in the same chain - search in ALL recipes, not just filtered
        const goldStandardRecipe = recipesToSearch.find(r => 
            r.chain_root_id === recipe.chain_root_id && 
            r.is_gold_standard === true
        );
        
        // Find the user's most recent fork in the chain (must be owned by current user)
        const userRecipesInChain = recipesToSearch.filter(r => 
            r.chain_root_id === recipe.chain_root_id && 
            r.owner_id === user?.uid &&
            r.id !== (goldStandardRecipe?.id || -1)
        );
        
        // Find the user's most recent fork (sorted by created_at, most recent first)
        const mostRecentUserFork = userRecipesInChain.length > 0
            ? userRecipesInChain.sort((a, b) => {
                const dateA = new Date(a.created_at || a.recipe_created_at || 0);
                const dateB = new Date(b.created_at || b.recipe_created_at || 0);
                return dateB - dateA; // Most recent first
            })[0]
            : null;
        
        // Check if there are any forks in the chain
        // Use forks_count if available (check both displayedRecipe and recipe), otherwise check if there are other recipes in the chain
        const hasForks = (recipeForksCount > 0) || 
            recipesToSearch.some(r => 
                r.chain_root_id === recipe.chain_root_id && 
                r.id !== recipe.id &&
                (goldStandardRecipe ? r.id !== goldStandardRecipe.id : true)
            );
        
        // Show button if: 1) there's a gold standard, AND 2) there are forks in the chain
        const isCurrentlyShowingGoldStandard = displayedRecipe.id === goldStandardRecipe?.id;
        const hasGoldStandard = !!goldStandardRecipe && hasForks;
        
        const handleCrownClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (isCurrentlyShowingGoldStandard) {
                // Currently showing gold standard - switch to user's most recent fork
                if (mostRecentUserFork) {
                    setShowingGoldStandard(prev => {
                        const newMap = new Map(prev);
                        newMap.set(recipe.id, mostRecentUserFork);
                        return newMap;
                    });
                }
            } else if (goldStandardRecipe) {
                // Currently showing non-gold-standard - show gold standard recipe
                setShowingGoldStandard(prev => {
                    const newMap = new Map(prev);
                    newMap.set(recipe.id, goldStandardRecipe);
                    return newMap;
                });
            }
        };
        
        return (
            <div className={`relative ${isForked ? 'group' : ''} ${isSelected ? 'ring-2 ring-blue-600' : ''} ${(isSchedulingSelected || isCompareSelected) ? 'ring-2 ring-[#1b263b]' : ''}`}>
                
                {/* Stacked window effect for forked recipes - positioned under the main card */}
                {isForked && (
                    <>
                        {/* Third window (back) - downsized with shadow on hover */}
                        <div className="absolute top-0 left-0 w-[calc(100%-12px)] h-[calc(100%-12px)] bg-white hairline transform translate-x-3 translate-y-3 pointer-events-none group-hover:shadow-popup transition-all duration-200"></div>
                        {/* Second window (middle) - downsized */}
                        <div className="absolute top-0 left-0 w-[calc(100%-12px)] h-[calc(100%-12px)] bg-white hairline transform translate-x-2 translate-y-2 pointer-events-none"></div>
                        {/* First window (front) - downsized */}
                        <div className="absolute top-0 left-0 w-[calc(100%-12px)] h-[calc(100%-12px)] bg-white hairline transform translate-x-1 translate-y-1 pointer-events-none"></div>
                    </>
                )}
                
                {/* Main recipe card */}
                <div className={`aspect-square bg-neutral-0 transition-all duration-200 cursor-pointer group flex flex-col relative ${isForked ? 'w-[calc(100%-12px)] h-[calc(100%-12px)] mt-0' : 'hover:shadow-popup'} ${(isSelected || (isSchedulingMode && selectedRecipeIds.has(recipe.id)) || isCompareSelected) ? 'ring-2 ring-[#1b263b] bg-[#EEEEE7]' : 'hairline'}`}
                    onClick={(e) => {
                        if (isSchedulingMode) {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleRecipeSelection(displayedRecipe.id, displayedRecipe);
                        } else if (showCompareSelectionModal) {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleCompareRecipe(displayedRecipe);
                        } else if (isMultiSelectMode) {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMultiSelectRecipe(displayedRecipe.id);
                        } else {
                            handleRecipeClick(displayedRecipe, e);
                        }
                    }}
                >
                    {/* Webpage browser bar for transferred recipes */}
                    {isTransferred && (
                        <div className="absolute top-0 left-0 right-0 h-6 bg-neutral-200 border-b border-neutral-900 flex items-center px-2 z-10">
                            <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-red-400 rounded-full border border-neutral-900"></div>
                                <div className="w-2 h-2 bg-yellow-400 rounded-full border border-neutral-900"></div>
                                <div className="w-2 h-2 bg-green-400 rounded-full border border-neutral-900"></div>
                            </div>
                            <div className="flex-1 mx-3">
                                <div className="h-4 bg-white border border-neutral-300 rounded-sm flex items-center px-2">
                                    <span className="text-xs text-neutral-600 truncate">{recipe.source_url ? new URL(recipe.source_url).hostname : 'recipe website'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                <div className={`flex-1 overflow-hidden relative z-10 ${isTransferred ? 'pt-6' : ''}`}>
                    <SignedImage
                        src={displayedRecipe.images && displayedRecipe.images.length > 0 ? displayedRecipe.images[0] : null}
                        alt={displayedRecipe.name || displayedRecipe.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        category={displayedRecipe.category}
                    />
                </div>
                
                <div className="p-3 flex-shrink-0 relative z-10">
                    <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight">
                        {displayedRecipe.name || displayedRecipe.title}
                    </Text>
                    
                    {(displayedRecipe.description || displayedRecipe.category) && (
                        <Text className="text-xs text-neutral-900 opacity-60 mb-2 line-clamp-2 leading-tight">
                            {displayedRecipe.description || displayedRecipe.category}
                        </Text>
                    )}
                    
                    <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-2">
                        <span className="text-neutral-900 opacity-60">{parsedIngredients.length ? `${parsedIngredients.length} ING` : '—'}</span>
                        <span className={`${nameStyle} font-medium`}>{displayName}</span>
                    </div>
                    
                    {/* Import status */}
                    {isTransferred && (
                        <div className="text-xs text-neutral-900 opacity-60 mb-2">
                            {recipe.source_url ? (
                                <>Imported from {new URL(recipe.source_url).hostname}</>
                            ) : recipe.author ? (
                                <>Imported · {recipe.author}</>
                            ) : (
                                <>Imported</>
                            )}
                        </div>
                    )}
                    {!isTransferred && !isForked && displayedRecipe.owner_id === user?.uid && (
                        <div className="text-xs text-neutral-900 opacity-60 mb-2">
                            Created by you
                        </div>
                    )}
                    
                    
                    {displayedRecipe.category && (
                        <div className="flex gap-1 flex-wrap">
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                {displayedRecipe.category}
                            </span>
                            {displayedRecipe.cooking_time && (
                                <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                    {formatCookingTime(displayedRecipe.cooking_time)}
                                </span>
                            )}
                        </div>
                    )}
                    
                </div>
                
                {/* Crown/Recent button to toggle between gold standard and most recent fork */}
                {hasGoldStandard && !displayedRecipe.isLineageView && (
                    <button
                        onClick={handleCrownClick}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute bottom-2 right-2 z-30 bg-neutral-0 hover:bg-neutral-50 transition-colors"
                        style={{ 
                            fontSize: '14px',
                            lineHeight: '1',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #0D0D0D',
                            borderRadius: '0',
                            padding: '2px'
                        }}
                        title={isCurrentlyShowingGoldStandard ? "Show most recent fork" : "Show gold standard recipe"}
                    >
                        {isCurrentlyShowingGoldStandard ? '🕐' : '👑'}
                    </button>
                )}
                    
                    {/* Back button for lineage views */}
                {displayedRecipe.isLineageView && displayedRecipe.originalRecipeId && (
                        <button
                        onClick={(e) => handleBackToOriginal(displayedRecipe.originalRecipeId, e)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        className="absolute bottom-2 right-2 text-neutral-900 hover:text-neutral-600 transition-colors z-30 p-0 m-0 border-none bg-transparent"
                            style={{ fontSize: '18px', fontFamily: 'monospace' }}
                            title="Back to original recipe"
                        >
                            ▼
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const RecipeListItem = ({ recipe }) => {
        // Parse ingredients if they're JSON strings
        const parsedIngredients = typeof recipe.ingredients === 'string' ? 
            JSON.parse(recipe.ingredients || '[]') : (recipe.ingredients || []);
        
        // Determine what to show in the third column
        // Check multiple indicators to ensure fork status is detected
        const isForked = (
            (recipe.is_fork || recipe.forker_name || recipe.parent_recipe_id) && 
            !recipe.is_original && 
            !recipe.is_transfer && 
            !recipe.isLineageView
        );
        
        // Get forker name - use created_by_name if forker_name is not set but recipe is a fork
        const forkerName = recipe.forker_name || 
                          (isForked && recipe.created_by_name ? recipe.created_by_name : null);
        const thirdColumnContent = isForked 
            ? { text: `Forked by ${forkerName || recipe.created_by_name || 'Unknown'}`, style: 'text-blue-600 font-medium' }
            : { text: new Date(recipe.created_at).toLocaleDateString(), style: 'text-neutral-900 opacity-60' };
            
        return (
            <div className="grid grid-cols-4 gap-4 py-2 px-0 hover:bg-neutral-50 transition-colors duration-120 cursor-pointer sm:border-none border border-neutral-200"
                style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}
                onClick={(e) => handleRecipeClick(recipe, e)}
            >
                <div className="flex items-center min-w-0 pl-2">
                    <Text className="font-medium text-neutral-900 truncate">
                        {recipe.name || recipe.title}
                    </Text>
                </div>
                
                <div className="flex items-center font-mono text-sm text-neutral-900 min-w-0 pl-2">
                    <div className="flex gap-1 min-w-0">
                        {recipe.category && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {recipe.category}
                            </span>
                        )}
                        {recipe.cooking_time && (
                            <span className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                {formatCookingTime(recipe.cooking_time)}
                            </span>
                        )}
                        {!recipe.category && !recipe.cooking_time && (
                            <span className="text-neutral-900 opacity-40 text-xs">—</span>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center pl-2">
                    <div className="flex flex-col">
                        <span className={`font-mono text-sm ${thirdColumnContent.style}`}>
                            {thirdColumnContent.text}
                        </span>
                    </div>
                </div>
                
                <div className="flex items-center pl-2">
                    <div className="flex space-x-1">
                        <Link 
                            to={`/recipes/${recipe.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 px-2 py-1 hover:opacity-60 transition-opacity"
                        >
                            VIEW
                        </Link>
                        <Link 
                            to={`/edit/${recipe.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                        >
                            EDIT
                        </Link>
                        <Link 
                            to={`/fork/${recipe.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                        >
                            FORK
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Authentication Required</Heading>
                        <Text>You need to be logged in to view your recipes.</Text>
                        <Link to="/login" className="nav-link mt-4 inline-block">LOGIN</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (loading || isFiltering) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <PixelArtLoader 
                            message={loading ? 'LOADING RECIPES' : 'PROCESSING RECIPES'}
                            subMessage={loading ? 'Fetching your culinary collection...' : 'Organizing your recipe library...'}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Error</Heading>
                        <Text>{error}</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
        {/* Floating Create Button */}
        {user && <FloatingCreateButton />}
        
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative">
                    {/* Controls Button - Bottom Right of Heading */}
                    <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 z-10 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setControlsExpanded((prev) => !prev)}
                            aria-expanded={controlsExpanded}
                            aria-controls={controlsPanelId}
                            aria-label={`${controlsExpanded ? 'Hide' : 'Show'} recipe controls`}
                            className="flex items-center justify-center w-10 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                        >
                            <span className="text-xl leading-none">{controlsExpanded ? '▴' : '▾'}</span>
                        </button>
                    </div>
                    
                    <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                        <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                            MY RECIPES
                        </h1>
                        <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                            YOUR RECIPES • {filteredRecipes.length} RECIPE{filteredRecipes.length !== 1 ? 'S' : ''}
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    {controlsExpanded && (
                        <div id={controlsPanelId} className="space-y-6 mb-6">
                            {/* Responsive Layout - Stack on mobile, side-by-side on larger screens */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                                {/* Left Group - View Toggle */}
                                <div className="flex items-center gap-2">
                                    {/* View Toggle */}
                                    <div className="flex items-center">
                                        <button
                                            type="button"
                                            onClick={() => setContentView('recipes')}
                                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                contentView === 'recipes' 
                                                    ? 'bg-neutral-900 text-neutral-0' 
                                                    : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                            }`}
                                        >
                                            RECIPES
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setContentView('collections')}
                                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                contentView === 'collections' 
                                                    ? 'bg-neutral-900 text-neutral-0' 
                                                    : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                            }`}
                                        >
                                            COLLECTIONS
                                        </button>
                                    </div>
                                </div>

                                {/* Right Group - All Other Controls - Responsive Layout */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
                                    {/* Category Filter */}
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="px-4 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm uppercase tracking-wider min-w-0"
                                        style={{maxWidth: '100px'}}
                                    >
                                        <option value="">CAT</option>
                                        {VALID_CATEGORIES.map((category) => (
                                            <option key={category} value={category}>
                                                {category.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Collection Filter */}
                                    <select
                                        value={selectedCollection}
                                        onChange={(e) => setSelectedCollection(e.target.value)}
                                        className="px-4 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm uppercase tracking-wider min-w-0"
                                        style={{maxWidth: '100px'}}
                                    >
                                        <option value="">COLL</option>
                                        <option value="no-collection">NONE</option>
                                        {collections.map((collection) => (
                                            <option key={collection.id} value={collection.id}>
                                                {collection.name.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Search Toggle */}
                                    <button
                                        type="button"
                                        onClick={() => setShowSearch(!showSearch)}
                                        className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                            searchTerm 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        🔍
                                    </button>

                                    {/* Multi-select toggle */}
                                    <button
                                        type="button"
                                        onClick={toggleMultiSelectMode}
                                        className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                            isMultiSelectMode 
                                                ? 'bg-blue-600 text-black hover:bg-blue-700' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        {isMultiSelectMode ? 'CANCEL' : 'SELECT'}
                                    </button>

                                    {/* View Mode Toggle - Only show for recipes view */}
                                    {contentView === 'recipes' && (
                                        <div className="flex items-center">
                                            <button
                                                type="button"
                                                onClick={() => setViewMode('grid')}
                                                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                    viewMode === 'grid' 
                                                        ? 'bg-neutral-900 text-neutral-0' 
                                                        : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                }`}
                                            >
                                                ⚏
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setViewMode('list')}
                                                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                    viewMode === 'list' 
                                                        ? 'bg-neutral-900 text-neutral-0' 
                                                        : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                }`}
                                            >
                                                ☰
                                            </button>
                                        </div>
                                    )}

                                    {/* Clear Filters */}
                                    {hasActiveFilters && (
                                        <button
                                            type="button"
                                            onClick={clearFilters}
                                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                                        >
                                            ✕
                                        </button>
                                    )}

                                </div>
                            </div>

                            {/* Search Bar - Hidden by default */}
                            {showSearch && (
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        placeholder="Search recipes..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-2 py-1 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        autoFocus
                                    />
                                </div>
                            )}

                            {/* Add Collection Form */}
                            {showAddCollection && (
                                <div className="bg-neutral-50 hairline p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Heading level={4}>Add New Collection</Heading>
                                        <button
                                            onClick={() => {
                                                setShowAddCollection(false);
                                                setNewCollectionName('');
                                            }}
                                            className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Collection name..."
                                            value={newCollectionName}
                                            onChange={(e) => setNewCollectionName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addCollection()}
                                            className="flex-1 px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                            autoFocus
                                        />
                                        <button
                                            onClick={addCollection}
                                            disabled={!newCollectionName.trim()}
                                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            ADD
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!controlsExpanded && hasActiveFilters && (
                        <div className="mb-3 text-xs md:text-sm font-mono text-neutral-900 opacity-60 flex items-center justify-between gap-2">
                            <span>Showing {filteredRecipes.length} of {recipes.length} recipes</span>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                            >
                                CLEAR
                            </button>
                        </div>
                    )}

                    {/* Bulk Actions Bar */}
                    {showBulkActions && (
                        <div className="mb-4 p-4 bg-blue-50 hairline">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <Text className="font-mono text-sm uppercase tracking-wider text-blue-900">
                                        {selectedRecipes.size} RECIPE{selectedRecipes.size !== 1 ? 'S' : ''} SELECTED
                                    </Text>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={selectAllRecipes}
                                            className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                        >
                                            SELECT ALL
                                        </button>
                                        <button
                                            onClick={clearSelection}
                                            className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                                        >
                                            CLEAR
                                        </button>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleBulkAddToCollection}
                                        className="px-4 py-2 text-xs font-mono uppercase tracking-wider bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                                    >
                                        ADD TO COLLECTION
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        className="px-4 py-2 text-xs font-mono uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 transition-colors"
                                    >
                                        DELETE
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    {contentView === 'collections' ? (
                        // Collections View
                        (() => {
                            // Get recipes not in any collection (showing only most recent versions)
                            const noCollectionRecipes = getRecipesByCollection('no-collection');
                            
                            // Create collections array with "No Collection" if there are unassigned recipes
                            const allCollections = [
                                ...(noCollectionRecipes.length > 0 ? [{
                                    id: 'no-collection',
                                    name: 'No Collection',
                                    created_at: new Date().toISOString()
                                }] : []),
                                ...collections
                            ];
                            
                            return (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {/* Add Collection Card - Always first */}
                                    <div 
                                        onClick={() => setShowAddCollection(true)}
                                        className="group cursor-pointer bg-neutral-0 hairline hover:bg-neutral-50 transition-colors duration-120 flex flex-col overflow-hidden"
                                    >
                                        <div className="flex-1 overflow-hidden relative z-10">
                                            <div className="w-full h-full flex flex-col justify-center items-center bg-neutral-50 group-hover:bg-neutral-100 transition-colors">
                                                <img 
                                                    src="/images/plus-icon.svg"
                                                    alt="Add Collection"
                                                    className="w-12 h-12 opacity-60 group-hover:opacity-100 transition-opacity"
                                                />
                                </div>
                                        </div>
                                        
                                        <div className="p-3 flex-shrink-0 relative z-10">
                                            <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight">
                                                Add Collection
                                            </Text>
                                            
                                            <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider">
                                                <span className="text-neutral-900 opacity-60">COLLECTION</span>
                                                <span className="text-blue-600 font-medium">CREATE</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Existing Collections */}
                                    {allCollections.map((collection) => (
                                        <CollectionCard 
                                            key={collection.id} 
                                            collection={collection} 
                                            collectionRecipes={getRecipesByCollection(collection.id)}
                                        />
                                    ))}
                                </div>
                            );
                        })()
                    ) : (
                        // Recipes View
                        filteredRecipes.length === 0 ? (
                            <div className="text-center py-12">
                                <Text className="mb-4">
                                    {recipes.length === 0 
                                        ? 'No recipes found. Click the plus tab to begin creating your first recipe!' 
                                        : 'No recipes match your search.'
                                    }
                                </Text>
                                {recipes.length > 0 && (
                                    <button onClick={clearFilters} className="nav-link">CLEAR FILTERS</button>
                                )}
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {filteredRecipes.map((recipe) => (
                                    <RecipeGridCard key={recipe.id} recipe={recipe} allRecipes={recipes} />
                                ))}
                            </div>
                        ) : (
                        <div className="px-0">
                            {/* Column Headers - Hidden on smallest screens */}
                            <div className="hidden sm:grid grid-cols-4 gap-4 mb-4 px-0" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TITLE
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TAGS
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    INFO
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    ACTIONS
                                </div>
                            </div>

                            {/* Table - Extended to edges */}
                            <div className="bg-neutral-0 sm:bg-transparent">
                                {filteredRecipes.map((recipe, index) => (
                                    <div key={recipe.id} className={index > 0 ? 'mt-2' : ''}>
                                        <RecipeListItem recipe={recipe} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        )
                    )}
                </div>

                {/* Popup Windows */}
                {openPopups.map((popup) => (
                    <PopupWindow
                        key={popup.id}
                        title={popup.recipe.name || popup.recipe.title}
                        images={popup.recipe.images}
                        onClose={() => closePopup(popup.id)}
                        style={popup.style}
                    />
                ))}


                {/* Bulk Add to Collection Modal */}
                {showAddToCollectionModal && (
                    <Modal isOpen={showAddToCollectionModal} onClose={() => setShowAddToCollectionModal(false)}>
                        <div className="p-6">
                            <Heading level={3} className="mb-4">Add to Collection</Heading>
                            <Text className="mb-4">
                                Add {selectedRecipes.size} recipe{selectedRecipes.size !== 1 ? 's' : ''} to a collection:
                            </Text>
                            
                            <div className="space-y-3 mb-6">
                                {collections.map(collection => (
                                    <button
                                        key={collection.id}
                                        onClick={() => setSelectedCollection(collection.id)}
                                        className={`w-full p-3 text-left transition-colors ${
                                            selectedCollection === collection.id 
                                                ? 'bg-blue-600 text-white' 
                                                : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50'
                                        }`}
                                    >
                                        {collection.name}
                                    </button>
                                ))}
                            </div>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowAddToCollectionModal(false)}
                                    className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                                >
                                    CANCEL
                                </button>
                                <button
                                    onClick={handleBulkAddToCollectionConfirm}
                                    disabled={!selectedCollection || addingToCollection}
                                    className="px-4 py-2 bg-blue-600 text-white font-mono text-sm uppercase tracking-wider hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {addingToCollection ? 'ADDING...' : 'ADD TO COLLECTION'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* Bulk Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
                        <div className="p-6">
                            <Heading level={3} className="mb-4 text-red-600">Delete Recipes</Heading>
                            
                            {(() => {
                                const hasForks = recipesToDelete.some(recipe => recipe.forks_count > 0);
                                return (
                                    <>
                                        <Text className="mb-4">
                                            Are you sure you want to delete {recipesToDelete.length} recipe{recipesToDelete.length !== 1 ? 's' : ''}?
                                        </Text>
                                        
                                        {hasForks && (
                                            <div className="mb-4 p-4 bg-yellow-50 hairline">
                                                <Text className="text-yellow-800 font-medium mb-2">⚠️ WARNING</Text>
                                                <Text className="text-yellow-800 text-sm">
                                                    You will delete all forks you have authored associated with these recipes. 
                                                    This action cannot be undone.
                                                </Text>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-2 mb-6">
                                            {recipesToDelete.map(recipe => (
                                                <div key={recipe.id} className="p-2 bg-neutral-50 hairline text-sm">
                                                    {recipe.name || recipe.title}
                                                    {recipe.forks_count > 0 && (
                                                        <span className="ml-2 text-blue-600">
                                                            ({recipe.forks_count} fork{recipe.forks_count !== 1 ? 's' : ''})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => setShowDeleteConfirm(false)}
                                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                                            >
                                                CANCEL
                                            </button>
                                            <button
                                                onClick={confirmBulkDelete}
                                                disabled={deleting}
                                                className="px-4 py-2 bg-red-600 text-white font-mono text-sm uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {deleting ? 'DELETING...' : 'DELETE RECIPES'}
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </Modal>
                )}
            </div>
        </div>
        <>
            {/* Education Modals */}
            <EducationModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create Recipe"
                description="Create a new recipe from scratch. Add ingredients, instructions, images, and all the details needed to cook your dish."
                image="/images/education/create-demo.png"
                details={[
                    "Add recipe name, description, and category",
                    "Enter ingredients with quantities and measurements",
                    "Write step-by-step cooking instructions",
                    "Upload images of your finished dish",
                    "Set cooking time, servings, and difficulty level",
                    "Save and organize your recipes"
                ]}
            />
            <EducationModal
                isOpen={showBasesModal}
                onClose={() => setShowBasesModal(false)}
                title="Bases"
                description="Browse and use recipe bases - foundational recipes that serve as starting points for creating variations. Bases are templates you can customize."
                image="/images/education/bases-demo.png"
                details={[
                    "Browse available recipe bases",
                    "Use bases as starting points for new recipes",
                    "Customize bases to create your own variations",
                    "Build recipes from proven foundations",
                    "Save time by starting with tested templates"
                ]}
            />
        </>
        </>
    );
};

export default Recipes;
